/**
 * AgentClient SDK — the client (adr-039).
 *
 * Ties together: a pure reducer (state), a Transport (delivers server events),
 * a MediaController (LiveKit room lifecycle), and an AgentBackend (REST
 * commands). Every transition flows through the reducer, so the UI is a pure
 * function of getSnapshot(). All three ports are interfaces → the whole client
 * is driven headless in tests with mocks (no browser, no phone).
 */
import { availabilityOf, initialCore, reduce, snapshot } from "./reducer";
import type {
  AgentEvent, AgentSnapshot, Availability, CallPhase, ChosenState, CommandKey, InteractionRef,
  SipCallStatus, Unsubscribe,
} from "./types";

/** Phases with a leg on the wire — hangup reaches all of them, and media is
 *  torn down when one is left. */
const CANCELLABLE: CallPhase[] = ["dialing", "connecting", "ringback", "connected", "hold"];

/** A throwable turned into something an agent can read. */
function reasonOf(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = String((err as { message?: unknown }).message ?? "").trim();
    if (m) return m;
  }
  const s = String(err ?? "").trim();
  return s && s !== "[object Object]" ? s : "unknown error";
}

const COMMAND_KEYS: CommandKey[] = ["dial", "accept", "reject", "hangup", "hold", "resume", "mute", "recording", "transfer", "wrap"];

/** Server→client event delivery (polling today; SSE/WSS later — adr-039). */
export interface Transport {
  start(): void;
  stop(): void;
}

/** Backend command port (REST). Mockable in tests. */
export interface AgentBackend {
  /** OUTBOUND (adr-050): originate a call, returning the room + agent WebRTC
   *  token to join. Optional — an inbound-only backend (agent desktop) omits it. */
  dial?(destination: string): Promise<{ room?: string; token?: string; url?: string; error?: string; call_id?: string }>;
  answer(interactionId: string): Promise<{ room?: string; token?: string; url?: string; error?: string; context?: InteractionRef }>;
  /** Decline a ringing offer (RONA): requeue it to another agent, excluding this
   *  one. No media is involved — the agent never joined the call. */
  reject(interactionId: string): Promise<void>;
  hold(interactionId: string, roomName: string): Promise<void>;
  resume(interactionId: string): Promise<void>;
  hangup(interactionId: string, roomName: string): Promise<void>;
  /** Caller-initiated end: move the orchestrator interaction to WRAP_UP. */
  disconnect(interactionId: string, reason: string): Promise<void>;
  setState(state: Availability, reason?: string | null): Promise<void>;
  pciPause(interactionId: string): Promise<void>;
  pciResume(interactionId: string): Promise<void>;
  transfer(interactionId: string, target: string, kind: "agent" | "queue" | "external", mode: "blind" | "warm"): Promise<void>;
  conference(interactionId: string, participants: string[]): Promise<void>;
  wrapup(interactionId: string, disposition: string, notes: string): Promise<void>;
  abandonWrapup(interactionId: string, disposition: string, notes: string): Promise<void>;
}

/** LiveKit room lifecycle port. Reports remote-initiated end via hooks. */
export interface MediaController {
  connect(creds: { url: string; token: string }, hooks: MediaHooks): Promise<{ room?: string }>;
  disconnect(): Promise<void>;
  setMuted(muted: boolean): Promise<void>;
  /**
   * Park / unpark this end of the call for HOLD.
   *
   * Hold is not mute and it is not "start some music". It is a promise that the
   * two parties are disconnected from each other: the caller hears music and
   * nothing else, and the agent can put the headset down and talk to a
   * colleague. Playing music over a live two-way path keeps neither half of
   * that promise — it only makes the eavesdropping harder to notice.
   *
   * The half a browser can guarantee is its own: stop publishing the
   * microphone, and stop playing what arrives. The other half — that the
   * CALLER is not receiving the agent's audio — cannot be asserted from here
   * and is enforced server-side by the subscription change in edge-media.
   */
  setHold(held: boolean): Promise<void>;
  sendDtmf(digit: string): void;
}
export interface MediaHooks {
  onRemoteEnded: () => void;
  /** OUTBOUND: the callee (SIP leg) joined the room → the call is answered.
   *
   *  NOT "a SIP participant appeared": LiveKit puts the SIP participant in the
   *  room the instant it starts dialling, long before anyone picks up, so
   *  presence alone would report every attempt as answered the moment it left.
   *  The adapter fires this on `sip.callStatus: active`. */
  onRemoteConnected?: () => void;
  /** OUTBOUND: carrier progress on the originating leg, mirrored from the SIP
   *  participant's `sip.callStatus` (dialing → ringing → active | hangup). */
  onProgress?: (status: SipCallStatus, reason?: string) => void;
}

export interface AgentClientOpts {
  backend: AgentBackend;
  media: MediaController;
  /** Factory receives the client's dispatch so the transport can emit events. */
  transport: (emit: (e: AgentEvent) => void) => Transport;
  /** Presence probe cadence (ms). 0 disables (tests). Default 15000. */
  heartbeatMs?: number;
  /**
   * Consecutive failed probes before the link is declared down. Default 2.
   *
   * Not 1: a single dropped request on a flaky network would bounce an agent
   * out of the queue mid-shift. Not 5 either — every probe that fails is a
   * window in which the ACD is still offering this agent calls that ring
   * nowhere, so the count buys tolerance at the cost of dead air for a caller.
   */
  linkFailureThreshold?: number;
  /** Injectable timers so tests run without real clocks. */
  setInterval?: (fn: () => void, ms: number) => number;
  clearInterval?: (id: number) => void;
}

export class AgentClient {
  private core = initialCore;
  private readonly subs = new Set<(s: AgentSnapshot) => void>();
  private readonly transport: Transport;
  private transportRunning = false;
  private heartbeat: number | null = null;
  private linkFailures = 0;
  private readonly inFlight = new Set<CommandKey>();   // commands currently running (idempotency)
  private readonly setI: (fn: () => void, ms: number) => number;
  private readonly clearI: (id: number) => void;

  constructor(private readonly o: AgentClientOpts) {
    this.setI = o.setInterval ?? ((fn, ms) => (globalThis.setInterval(fn, ms) as unknown as number));
    this.clearI = o.clearInterval ?? ((id) => globalThis.clearInterval(id));
    this.transport = o.transport((e) => this.dispatch(e));
  }

  // -- subscription --------------------------------------------------------
  getSnapshot(): AgentSnapshot {
    const pending = {} as Record<CommandKey, boolean>;
    for (const k of COMMAND_KEYS) pending[k] = this.inFlight.has(k);
    return { ...snapshot(this.core), pending };
  }

  /** Run a command under an in-flight lock keyed by name: a second invocation
   *  while one is running is a no-op (idempotent buttons). Notifies on lock
   *  change so the UI can disable the button while it's pending. */
  private async once(key: CommandKey, fn: () => Promise<void>): Promise<void> {
    if (this.inFlight.has(key)) return;
    this.inFlight.add(key); this.notify();
    try { await fn(); }
    finally { this.inFlight.delete(key); this.notify(); }
  }
  subscribe(cb: (s: AgentSnapshot) => void): Unsubscribe {
    this.subs.add(cb); cb(this.getSnapshot());
    return () => this.subs.delete(cb);
  }
  private notify() { const s = this.getSnapshot(); this.subs.forEach((cb) => cb(s)); }

  /** Apply an event through the reducer and run the resulting side effects. */
  dispatch(e: AgentEvent) {
    const prev = this.core;
    const next = reduce(prev, e);
    if (next === prev) return;            // invalid/stale event → no-op (race-immune)
    this.core = next;
    this.onTransition(prev, next);
    this.notify();
  }

  private onTransition(prev: typeof this.core, next: typeof this.core) {
    // Tear media down when leaving an active/originating call (agent hangup,
    // caller drop, blind transfer, outbound cancel). Media for accept()/dial() is
    // connected by the command itself.
    const active = (p: CallPhase) => CANCELLABLE.includes(p);
    if (active(prev.phase) && !active(next.phase)) void this.o.media.disconnect().catch(() => {});

    // Push presence whenever the derived availability changes.
    const a0 = availabilityOf(prev), a1 = availabilityOf(next);
    // Push a real presence change — but never the OFFLINE that merely reflects a
    // lost link. On a flapping connection one of those could actually land, and
    // the agent would be signed off on the backend for good: the link comes
    // back, the client re-asserts `chosen`, and the two disagree about who did
    // it. The probe re-asserts the chosen state on recovery instead.
    const linkInducedOffline = a1 === "OFFLINE" && next.link === "down";
    if (a1 !== a0 && !linkInducedOffline) void this.o.backend.setState(a1).catch(() => {});

    this.reconcileTransport();
  }

  /** Poll for offers only while ready to receive one (idle/ringing & available).
   *  Stops during connected/hold/wrap so an offer can't arrive mid-call. */
  private reconcileTransport() {
    // `link` as well as `chosen`: polling for offers we could not accept is
    // pointless traffic against a backend we cannot reach, and an offer that
    // did somehow arrive would ring for an agent whose Accept is going to fail.
    // The presence probe keeps running regardless — it is the only thing that
    // can notice the link coming back.
    const ready = this.core.chosen === "AVAILABLE"
      && this.core.link === "up"
      && (this.core.phase === "idle" || this.core.phase === "ringing");
    if (ready && !this.transportRunning) { this.transport.start(); this.transportRunning = true; }
    else if (!ready && this.transportRunning) { this.stopTransport(); }
  }

  private stopTransport() {
    if (this.transportRunning) { this.transport.stop(); this.transportRunning = false; }
  }

  // -- lifecycle -----------------------------------------------------------
  /**
   * Start the presence probe.
   *
   * This re-asserts availability AND is the liveness check: its success or
   * failure is what decides whether this desktop still has a backend. It used
   * to be `setState(a).catch(() => {})` — the failure was swallowed whole, so a
   * desktop that had lost the backend went on showing AVAILABLE indefinitely
   * while the ACD kept routing to it. Every one of those calls rang nowhere.
   *
   * The probe keeps running while the link is down; it is the only thing that
   * can notice recovery, since the offer poll is stopped whenever the agent is
   * not AVAILABLE — and a down link makes them not AVAILABLE.
   */
  start() {
    if (this.o.heartbeatMs === 0) return;
    const ms = this.o.heartbeatMs ?? 15000;
    this.heartbeat = this.setI(() => void this.probe(), ms);
  }

  /** One presence probe. Exposed for deterministic tests (no real clocks). */
  async probe(): Promise<void> {
    // While down, assert the state the agent CHOSE, not the derived one — the
    // derived one is OFFLINE precisely because the link is down, and sending
    // that would make recovery indistinguishable from signing off.
    const claim = this.core.link === "down" ? this.core.chosen : availabilityOf(this.core);
    if (claim === "OFFLINE" && this.core.link === "up") return;   // nothing to assert
    try {
      await this.o.backend.setState(claim);
      this.linkFailures = 0;
      this.dispatch({ type: "link_restored" });
    } catch {
      this.linkFailures += 1;
      const threshold = this.o.linkFailureThreshold ?? 2;
      if (this.linkFailures >= threshold) this.dispatch({ type: "link_lost" });
    }
  }
  stop() {
    if (this.heartbeat !== null) { this.clearI(this.heartbeat); this.heartbeat = null; }
    if (this.transportRunning) { this.transport.stop(); this.transportRunning = false; }
  }

  // -- commands ------------------------------------------------------------
  setAvailability(v: ChosenState) { this.dispatch({ type: "set_availability", value: v }); }

  /**
   * Report backend reachability, from whoever is running the presence probe.
   *
   * The probe is app-wide (an agent parked on chat is still routable), so it
   * lives above this client and pushes the answer down rather than each surface
   * running one of its own.
   */
  setLink(link: "up" | "down") {
    this.dispatch({ type: link === "down" ? "link_lost" : "link_restored" });
  }

  /** OUTBOUND (adr-050): originate a call. Only from idle, and only if the
   *  backend supports dialing. Enters `dialing`; the media controller reports the
   *  callee joining via onRemoteConnected → `answered` (connected). */
  dial(destination: string) {
    if (this.core.phase !== "idle" || !this.o.backend.dial || !destination.trim()) return Promise.resolve();
    return this.once("dial", async () => {
      let res;
      try { res = await this.o.backend.dial!(destination.trim()); }
      catch (err) {
        this.dispatch({ type: "dial_failed", reason: reasonOf(err) });
        return;
      }
      // A refusal has to be SAID. Returning quietly here is what made a blocked
      // dial indistinguishable from a click that never registered.
      if (res.error) { this.dispatch({ type: "dial_failed", reason: res.error }); return; }
      if (!res.url || !res.token) {
        this.dispatch({ type: "dial_failed", reason: "the call was placed but no media session came back, so there is nothing to listen to" });
        return;
      }
      this.dispatch({ type: "dial_started", interaction: { interaction_id: res.call_id ?? `ob-${destination}`, ani: destination, room_name: res.room } });
      try {
        await this.o.media.connect({ url: res.url, token: res.token }, {
          onRemoteEnded: () => this.onRemoteEnded(),
          onRemoteConnected: () => this.dispatch({ type: "answered", room: res.room }),
          onProgress: (status, reason) => this.dispatch({ type: "call_progress", status, reason }),
        });
      } catch (err) {
        // NOT "the control plane is fine even if media fails". The leg is out
        // and the agent cannot hear it: that is a failed call, and leaving the
        // desk parked in `dialing` strands it there — every later dial is
        // refused by the idle-only guard, with nothing on screen to say so.
        this.dispatch({ type: "dial_failed", reason:
          `could not join the call audio: ${reasonOf(err)}` });
        void this.o.media.disconnect().catch(() => {});
      }
    });
  }

  accept() {
    // Guarded so a double-click (or a click while the answer round-trip is still
    // in flight) answers exactly once — otherwise two accepts each open a
    // LiveKit room with the same identity; LiveKit kicks the first as a
    // duplicate → spurious caller_disconnected → wrap-up, and the orphaned room
    // keeps the caller's media flowing after hangup.
    const ix = this.core.phase === "ringing" ? this.core.interaction : null;
    if (!ix) return Promise.resolve();
    return this.once("accept", async () => {
      // Stop polling before the round-trip so an in-flight tick can't mistake
      // the interaction leaving RINGING_AGENT for a re-route.
      this.stopTransport();
      let res;
      try { res = await this.o.backend.answer(ix.interaction_id); }
      catch { this.dispatch({ type: "offer_rerouted" }); return; }
      if (res.error) { this.dispatch({ type: "offer_rerouted" }); return; }
      if (res.url && res.token) {
        try {
          await this.o.media.connect({ url: res.url, token: res.token },
            { onRemoteEnded: () => this.onRemoteEnded() });
        } catch { /* control plane is fine even if media fails; still go connected */ }
      }
      this.dispatch({ type: "answered", room: res.room });
    });
  }

  /** Decline a ringing inbound offer (RONA, R5.6): tell the backend to requeue it
   *  (this agent excluded) and return to idle, ready for the next call. Valid only
   *  while ringing — there's no agent media yet to tear down. Distinct from
   *  hangup(), which ends a live/originating call and moves to wrap-up. */
  reject() {
    const ix = this.core.phase === "ringing" ? this.core.interaction : null;
    if (!ix) return Promise.resolve();
    return this.once("reject", async () => {
      // Stop polling before the round-trip so an in-flight tick can't mistake the
      // interaction leaving RINGING_AGENT for a fresh re-route (mirrors accept()).
      this.stopTransport();
      await this.o.backend.reject(ix.interaction_id).catch(() => {});
      this.dispatch({ type: "offer_rerouted" });   // ringing → idle
    });
  }

  hangup() {
    const ix = this.activeInteraction();   // connected/hold OR an originating outbound leg
    if (!ix) return Promise.resolve();
    return this.once("hangup", async () => {
      await this.o.backend.hangup(ix.interaction_id, ix.room_name ?? "").catch(() => {});
      this.dispatch({ type: "agent_hangup" });
    });
  }

  /** Put the caller on hold: park the media, then move the call.
   *
   *  The media is parked FIRST and deliberately. The backend hop starts the
   *  music and cuts the caller's subscription to this agent, but it is a
   *  round trip — and for however long it takes, an agent who has pressed Hold
   *  and started speaking is being heard. Muting the microphone is local and
   *  immediate, so it happens before anything is awaited. */
  hold() {
    const ix = this.core.phase === "connected" ? this.core.interaction : null;
    if (!ix) return Promise.resolve();
    return this.once("hold", async () => {
      await this.o.media.setHold(true).catch(() => {});
      await this.o.backend.hold(ix.interaction_id, ix.room_name ?? "").catch(() => {});
      this.dispatch({ type: "held" });
    });
  }
  /** Take the caller off hold. The reverse order, for the same reason: the
   *  music stops and the caller is re-subscribed before this end starts
   *  publishing again, so the agent's first word is not spoken over music. */
  resume() {
    const ix = this.core.phase === "hold" ? this.core.interaction : null;
    if (!ix) return Promise.resolve();
    return this.once("resume", async () => {
      await this.o.backend.resume(ix.interaction_id).catch(() => {});
      await this.o.media.setHold(false).catch(() => {});
      this.dispatch({ type: "resumed" });
    });
  }

  setMuted(muted: boolean) {
    if (this.core.phase !== "connected" && this.core.phase !== "hold") return Promise.resolve();
    return this.once("mute", async () => {
      try { await this.o.media.setMuted(muted); this.dispatch({ type: "muted", value: muted }); }
      catch { /* keep prior mute state if media op fails */ }
    });
  }
  toggleMute() { return this.setMuted(!this.core.muted); }

  /** R10.2 PCI/PII pause — mutes the recording, the live transcript and the
   *  on-screen keypad echo together.
   *
   *  Deliberately NOT optimistic, unlike mute/hold: the agent reads a card number
   *  out loud on the strength of the badge this flips. Showing "paused" before the
   *  media path has confirmed it would mean flashing a promise we might be about
   *  to take back — so the state changes only once the server says the capture
   *  gate is actually closed (edge-media-service fails the request otherwise).
   */
  toggleRecording() {
    const ix = this.inCallInteraction();
    if (!ix) return Promise.resolve();
    return this.once("recording", async () => {
      const next = !this.core.recordingPaused;
      try {
        await (next ? this.o.backend.pciPause(ix.interaction_id)
                    : this.o.backend.pciResume(ix.interaction_id));
        this.dispatch({ type: "recording_paused", value: next });
      } catch (e) {
        // The pause state is left alone — a failed pause is NOT paused, and a
        // failed resume is still paused, so what is shown stays true. But the
        // failure is surfaced rather than swallowed: a control that visibly does
        // nothing when pressed is how an agent ends up reading a card number
        // onto a recording that never stopped.
        this.dispatch({
          type: "recording_toggle_failed",
          message: next
            ? "Recording is NOT paused — the media path could not be muted. Do not "
              + "read out sensitive details; try again."
            : "Recording is still paused — it could not be resumed.",
        });
      }
    });
  }

  transfer(target: string, kind: "agent" | "queue" | "external", mode: "blind" | "warm") {
    const ix = this.inCallInteraction();
    if (!ix || !target.trim()) return Promise.resolve();
    return this.once("transfer", async () => {
      this.dispatch({ type: "transfer_started" });
      try {
        await this.o.backend.transfer(ix.interaction_id, target.trim(), kind, mode);
        if (mode === "blind") { void this.o.media.disconnect().catch(() => {}); this.dispatch({ type: "blind_transferred" }); }
        else this.dispatch({ type: "conferenced" });   // warm: stays on the call
      } catch { this.dispatch({ type: "transfer_failed" }); }
    });
  }

  conference(participant: string) {
    const ix = this.inCallInteraction();
    if (!ix || !participant.trim()) return Promise.resolve();
    return this.once("transfer", async () => {
      this.dispatch({ type: "transfer_started" });
      try { await this.o.backend.conference(ix.interaction_id, [participant.trim()]); this.dispatch({ type: "conferenced" }); }
      catch { this.dispatch({ type: "transfer_failed" }); }
    });
  }

  sendDtmf(digit: string) {
    if (this.core.phase === "connected" || this.core.phase === "hold") this.o.media.sendDtmf(digit);
  }

  completeWrap(disposition: string, notes: string) {
    if (this.core.phase !== "wrap") return Promise.resolve();
    return this.once("wrap", async () => {
      const ix = this.core.interaction;
      const abandoned = this.core.wrapReason === "caller_abandoned_ringing";
      if (ix) {
        // An abandoned-while-ringing call is already terminal → annotate only.
        const done = abandoned
          ? this.o.backend.abandonWrapup(ix.interaction_id, disposition, notes)
          : this.o.backend.wrapup(ix.interaction_id, disposition, notes);
        await done.catch(() => {});
      }
      this.dispatch({ type: "wrap_completed" });
    });
  }

  /** LiveKit says the caller's leg (or the room) ended. Move the orchestrator
   *  interaction to WRAP_UP, then transition. Guarded by inCallInteraction() so
   *  our own hangup-triggered room close is a no-op (already wrapping). */
  private onRemoteEnded() {
    const ix = this.activeInteraction();
    if (!ix) return;
    void this.o.backend.disconnect(ix.interaction_id, "caller_disconnected").catch(() => {});
    this.dispatch({ type: "caller_disconnected" });
  }

  private inCallInteraction(): InteractionRef | null {
    return this.core.phase === "connected" || this.core.phase === "hold" ? this.core.interaction : null;
  }

  /** connected/hold OR an originating outbound leg (dialing) — the set of phases
   *  where hanging up / a remote drop is meaningful. */
  /** connected/hold OR an outbound leg still ringing out — every phase where
   *  there is something to hang up. All three pre-answer phases count: an agent
   *  who has just dialled a wrong number must be able to stop it, and could
   *  not, because this listed only `dialing` while the desk had moved on to
   *  `connecting` or `ringback`. */
  private activeInteraction(): InteractionRef | null {
    return CANCELLABLE.includes(this.core.phase) ? this.core.interaction : null;
  }
}
